<template>
  <div
    :class="`formulate-input-element formulate-input-element--${context.type} ${hasRequiredError}`"
    :data-type="context.type"
    @click="handleOpenModal"
  >
    <div
      ref="inputContainer"
      class="mc-input d-inline-flex"
      :class="{
        'input-md': size == 'md',
        'input-sm': size == 'sm',
        __disabled: disabled,
        __multiple: hasSelected,
      }"
    >
      <!-- This input field is only used to show placeholder using regular input style -->
      <!-- If no value is selected -->
      <input
        v-if="!hasSelected"
        :placeholder="attrs.placeholder || context.label"
        style="border: 0; width: 200px"
        readonly
      />
      <div v-else-if="multiple" ref="tagsContainer" class="d-flex gap-2 flex-wrap">
        <template v-for="(value, index) in tagValue">
          <div
            v-if="index < visibleTagCount"
            :key="index"
            ref="tagElements"
            v-b-tooltip.hover="value.length > 25 ? value : null"
            :class="value === attrs.disabledItem ? '__tag-disabled' : '__tag'"
          >
            <span>{{ value.length > 25 ? value.slice(0, 22) + '...' : value }}</span>
            <i
              v-if="value !== attrs.disabledItem && !isItemDisabled(value)"
              :class="['icon-outline_close', { '__icon-disabled': disabled }]"
              @click.stop="onIconClick(value)"
            />
          </div>
          <div
            v-else-if="tagValue.length > visibleTagCount && index == visibleTagCount"
            :key="index"
            class="__count"
          >
            <i class="icon-fill_plus" />
            <span>{{
              attrs.hasDisabledItem
                ? context.model.length - visibleTagCount
                : context.model.length - visibleTagCount
            }}</span>
          </div>
        </template>
      </div>
      <div
        v-else-if="localFullObject?.[displayedBy]"
        ref="singleValueDisplay"
        v-b-tooltip.hover="isSingleValueOverflowed ? formatDisplayText(localFullObject) : null"
      >
        {{ formatDisplayText(localFullObject) }}
      </div>
      <div
        v-else
        ref="singleValueDisplay"
        v-b-tooltip.hover="isSingleValueOverflowed ? context.model : null"
      >
        {{ context.model }}
      </div>

      <i class="icon-fill_menu d-flex align-items-center" />
    </div>

    <ModalValueHelp
      :context="context"
      :attrs="attrs"
      :multiple="multiple"
      :fullObject="localFullObject"
      @update:fullObject="onUpdate"
      @addNewData="$emit('addNewData')"
      @update:header-filter-full-object="(payload) => $emit('update:header-filter-full-object', payload)"
    >
      <template v-for="(val, key) in slotFields" #[val.name]="{ item, value }">
        <!-- eslint-disable-next-line vue/require-component-is -->
        <component :is="val.slotComponent" :key="key" :item="item" :value="value" :field="val" />
      </template>
    </ModalValueHelp>
  </div>
</template>

<script>
import ModalValueHelp from './components/ModalValueHelp'
import isEmpty from 'lodash/isEmpty'

export default {
  components: {
    ModalValueHelp,
  },
  props: {
    context: {
      type: Object,
      required: true,
    },

    size: {
      type: String,
      default: 'md',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    displayedBy: {
      type: String,
    },

    fullObject: {},

    canBeNull: {
      type: Boolean,
      default: false,
    },

    identifier: {
      type: String,
      default: 'id',
    },
  },
  emits: ['update:fullObject', 'addNewData'],
  data() {
    return {
      localFullObject: {},
      visibleTagCount: 2, // Default to showing 2 tags
      resizeObserver: null,
      isSingleValueOverflowed: false,
      singleValueResizeObserver: null,
    }
  },
  computed: {
    attrs() {
      return this.context.attributes
    },

    slotFields() {
      return this.attrs?.tableFields ? this.attrs.tableFields.filter((v) => v.isSlot) : []
    },

    // add classes if it has validation error
    hasRequiredError() {
      if (this.context.visibleValidationErrors.includes('Wajib diisi'))
        return 'formulate-input-error__required'
      return ''
    },

    // if there is selected value(s)
    hasSelected() {
      if (this.attrs.hasDisabledItem) return true
      if (this.multiple) return this.context.model.length !== 0
      return this.context.model !== ''
    },

    tagValue() {
      let objects = this.localFullObject || this.context.model
      if (!Array.isArray(objects)) objects = [objects]
      const value = objects.map((v) => this.formatDisplayText(v))
      if (this.attrs.hasDisabledItem) {
        const disabledItem = this.attrs.disabledItem
        value.unshift(disabledItem)
      }
      return value.filter((v) => v !== null && v !== undefined && v !== '')
    },
  },
  watch: {
    fullObject: {
      handler(val) {
        this.localFullObject = this.multiple ? [...val] : { ...val }
        this.$nextTick(() => {
          this.checkSingleValueOverflow()
        })
      },
      deep: true,
      immediate: true,
    },
    'context.model': {
      handler() {
        this.$nextTick(() => {
          this.checkSingleValueOverflow()
        })
      },
      immediate: true,
    },
    tagValue: {
      handler() {
        this.$nextTick(() => {
          this.calculateVisibleTags()
        })
      },
      immediate: true,
    },
  },
  mounted() {
    this.calculateVisibleTags()
    this.checkSingleValueOverflow()

    // Observe resize of the input container
    if (this.$refs.inputContainer) {
      this.resizeObserver = new ResizeObserver(() => {
        this.calculateVisibleTags()
      })
      this.resizeObserver.observe(this.$refs.inputContainer)
    }

    // Observe resize of the single value display
    this.$nextTick(() => {
      let el = this.$refs.singleValueDisplay
      if (el) {
        if (Array.isArray(el) && el.length > 0) {
          el = el[0]
        }
        if (el) {
          this.singleValueResizeObserver = new ResizeObserver(() => {
            this.checkSingleValueOverflow()
          })
          this.singleValueResizeObserver.observe(el)
        }
      }
    })
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    if (this.singleValueResizeObserver) {
      this.singleValueResizeObserver.disconnect()
    }
  },
  methods: {
    isEmpty,
    checkSingleValueOverflow() {
      this.$nextTick(() => {
        let el = this.$refs.singleValueDisplay
        if (el) {
          // Jika ref adalah array, ambil elemen pertama
          if (Array.isArray(el) && el.length > 0) {
            el = el[0]
          }
          if (el) {
            this.isSingleValueOverflowed = el.scrollWidth > el.clientWidth
          } else {
            this.isSingleValueOverflowed = false
          }
        } else {
          this.isSingleValueOverflowed = false
        }
      })
    },
    formatDisplayText(item) {
      if (!item) return ''
      const mainValue = item[this.displayedBy]
      const subValue = this.attrs.subKey ? item[this.attrs.subKey] : null
      return subValue ? `${mainValue} (${subValue})` : mainValue
    },
    // handle dynamic on the multiple tag value of value help v2
    calculateVisibleTags() {
      if (!this.multiple || !this.$refs.inputContainer || !this.tagValue.length) {
        return
      }

      this.$nextTick(() => {
        const container = this.$refs.inputContainer
        if (!container) return

        const containerWidth = container.clientWidth
        const iconWidth = 40 // Approximate width for the menu icon
        const gapWidth = 8 // Gap between tags (from flex gap-2)
        const countBadgeWidth = 50 // Approximate width for the +N badge

        let availableWidth = containerWidth - iconWidth - 10 // 10px for padding

        // Measure actual tag widths
        let totalWidth = 0
        let visibleCount = 0

        // Create temporary elements to measure tag widths
        const tempContainer = document.createElement('div')
        tempContainer.style.position = 'absolute'
        tempContainer.style.visibility = 'hidden'
        tempContainer.style.whiteSpace = 'nowrap'
        document.body.appendChild(tempContainer)

        for (let i = 0; i < this.tagValue.length; i++) {
          const value = this.tagValue[i]
          const displayText = value.length > 25 ? value.slice(0, 22) + '...' : value

          // Create a temporary tag element
          const tempTag = document.createElement('div')
          tempTag.className = '__tag'
          tempTag.style.cssText =
            'background: #00a65a; color: white; padding: 4px 8px; border-radius: 4px; display: inline-flex; align-items: center; font-size: 12px;'

          const span = document.createElement('span')
          span.textContent = displayText
          tempTag.appendChild(span)

          // Add close icon width if applicable
          if (value !== this.attrs?.disabledItem && !this.isItemDisabled(value)) {
            const icon = document.createElement('i')
            icon.className = 'icon-outline_close'
            icon.style.cssText = 'margin-left: 4px;'
            tempTag.appendChild(icon)
          }

          tempContainer.appendChild(tempTag)
          const tagWidth = tempTag.offsetWidth
          tempContainer.removeChild(tempTag)

          // Check if we can fit this tag
          const widthNeeded = totalWidth + tagWidth + (visibleCount > 0 ? gapWidth : 0)

          // If we have more tags after this, reserve space for the count badge
          if (i < this.tagValue.length - 1) {
            if (widthNeeded + countBadgeWidth + gapWidth <= availableWidth) {
              totalWidth = widthNeeded
              visibleCount++
            } else {
              break
            }
          } else {
            // Last tag, no need for count badge
            if (widthNeeded <= availableWidth) {
              totalWidth = widthNeeded
              visibleCount++
            }
          }
        }

        document.body.removeChild(tempContainer)

        const configuredVisibleTagCount = Number(this.attrs?.visibleTagCount)
        const hasConfiguredVisibleTagCount =
          Number.isInteger(configuredVisibleTagCount) && configuredVisibleTagCount > 0

        // Ensure at least 1 tag is visible if there are tags
        this.visibleTagCount = hasConfiguredVisibleTagCount
          ? Math.min(Math.max(1, visibleCount), configuredVisibleTagCount)
          : Math.max(1, visibleCount)
      })
    },
    onUpdate($event) {
      // Some orders have null customer id but still have customer name
      // So we need to handle when customer id is null but when user click apply button without selecting new customer, it won't be changed
      if (this.canBeNull && this.fullObject[this.identifier] === null && isEmpty($event)) {
        this.$emit('update:fullObject', this.fullObject)
        return
      }
      this.localFullObject = this.multiple ? [...$event] : { ...$event }
      this.$emit('update:fullObject', $event)
    },
    handleOpenModal() {
      if (this.disabled) return
      this.$bvModal.show(`${this.attrs.name}-modal-value-help`)
    },
    onIconClick(value) {
      if (this.disabled) return
      this.handleRemoveTag(value)
    },

    handleRemoveTag(value) {
      if (this.disabled) return
      if (!this.multiple || !Array.isArray(this.localFullObject)) return

      let itemIndex = -1
      if (this.attrs.trackBy) {
        itemIndex = this.localFullObject.findIndex((item) => {
          return item[this.attrs.trackBy] === value || item[this.displayedBy] === value
        })
      } else {
        itemIndex = this.localFullObject.findIndex((item) => {
          return item[this.displayedBy] === value
        })
      }

      if (itemIndex === -1) {
        return
      }

      const updatedFullObject = [...this.localFullObject]
      updatedFullObject.splice(itemIndex, 1)

      const updatedModel = [...this.context.model]
      updatedModel.splice(itemIndex, 1)

      this.localFullObject = updatedFullObject
      this.context.model = updatedModel

      this.$emit('update:fullObject', updatedFullObject)
    },

    isItemDisabled(value) {
      const item = this.localFullObject.find((obj) => obj[this.displayedBy] === value)
      return item?.disableCheckbox || false
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
